import {
  ChangeDetectionStrategy,
  Component,
  Output,
  EventEmitter,
    DestroyRef,
  inject,
  OnInit,
  input,
  signal,
  computed,
  type InputSignal,
  type WritableSignal,
  type Signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { merge, Subject, type Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map } from "rxjs/operators";
import { CommonModule } from "@angular/common";
import { Router, RouterLink, NavigationEnd } from "@angular/router";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { CourseModel } from "@core/models/course.model";
import { MyIcon } from "@shared/classes/my-icon";
import { Location } from "@angular/common";
import { ThemeService } from "@shared/services/theme/theme.service";
import { SessionUserDataService } from "@shared/services/user-data/session-user-data.service";
import { UserAssignmentNavigationService } from "@core/services/user-assignment-navigation.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
import { NotificationBellComponent } from "@shared/components/notification-bell/notification-bell.component";
import { MobileSecondaryMenuComponent } from "@shared/components/mobile-secondary-menu/mobile-secondary-menu.component";
import { PROFILE_AVATAR_FALLBACK_SRC } from "@shared/constants/image-fallback.constants";
import { LAYOUT_MOBILE_BREAKPOINT_PX } from "@shared/constants/layout-breakpoints.constants";
import { ImageFallbackDirective } from "@shared/directives/image-fallback.directive";
import { NgxDynamicSkeletonDirective } from "ngx-dynamic-skeleton";
import { HEADER_SEEKER_DEBOUNCE_MS } from "@core/consts/header.constants";

import { ButtonDirective } from "@shared/directives/button.directive";
@Component({
  selector: "app-header-page",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./header-page.component.html",
  styleUrls: ["./header-page.component.scss"],
  host: {
    "(window:resize)": "onResize($event)",
  },
  imports: [ButtonDirective, 
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    FontAwesomeModule,
    TranslateModule,
    NotificationBellComponent,
    MobileSecondaryMenuComponent,
    ImageFallbackDirective,
    NgxDynamicSkeletonDirective,
  ],
})
export class HeaderPageComponent implements OnInit {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly seekerSubmit$: Subject<void> = new Subject<void>();
  private readonly router: Router = inject(Router);
  private readonly location: Location = inject(Location);
  readonly themeService: ThemeService = inject(ThemeService);
  private readonly sessionUserDataService: SessionUserDataService = inject(SessionUserDataService);
  private readonly userAssignmentNavigation: UserAssignmentNavigationService = inject(UserAssignmentNavigationService);

  public icon: MyIcon = new MyIcon();

  loadingRouteConfig!: boolean;
  isOnline: boolean = navigator.onLine;


  readonly avatar: Signal<string | undefined> = computed((): string | undefined => {
    const url: string | undefined = this.sessionUserDataService.user()?.avatarUrl;
    return url && url !== "" ? url : undefined;
  });
  public readonly profileAvatarFallback: string = PROFILE_AVATAR_FALLBACK_SRC;
  readonly name: Signal<string | undefined> = computed(
    (): string | undefined => this.sessionUserDataService.user()?.firstname,
  );
  readonly lastName: Signal<string | undefined> = computed(
    (): string | undefined => this.sessionUserDataService.user()?.lastname,
  );
  public coursesForm: FormGroup<{ seeker: FormControl<string | null> }> = new FormGroup({
    seeker: new FormControl(""),
  });
  public loading: boolean = false;
  public isSearchOpen: boolean = false;
  public readonly isMobile: WritableSignal<boolean> = signal<boolean>(
    window.innerWidth <= LAYOUT_MOBILE_BREAKPOINT_PX,
  );
  public readonly isCompactHeader: WritableSignal<boolean> = signal<boolean>(
    window.innerWidth <= 768,
  );

  public bell2: boolean = false;

  readonly showBack: InputSignal<boolean> = input<boolean>(false);
  readonly currentPage: InputSignal<string> = input<string>("");
  readonly routeBackPage: InputSignal<string> = input<string>("");
  readonly hasSearch: InputSignal<boolean> = input<boolean>(true);
  readonly bell: InputSignal<boolean> = input<boolean>(false);
  readonly isForumHelpOrCatalogue: InputSignal<boolean> = input<boolean>(false);

  readonly courses: InputSignal<CourseModel[]> = input<CourseModel[]>([]);
  readonly originalCourses: InputSignal<CourseModel[]> = input<CourseModel[]>([]);
  readonly completedCourses: InputSignal<CourseModel[]> = input<CourseModel[]>([]);
  readonly initiatedCourses: InputSignal<CourseModel[]> = input<CourseModel[]>([]);
  readonly unstartedCourses: InputSignal<CourseModel[]> = input<CourseModel[]>([]);

  readonly showBell: WritableSignal<boolean> = signal<boolean>(false);
  readonly showForumSearch: WritableSignal<boolean> = signal<boolean>(false);
  readonly searchPlaceholderKey: WritableSignal<string> = signal<string>("header.placeholderSearchCourse");

  readonly showCompactBack: Signal<boolean> = computed<boolean>(
    (): boolean => this.isCompactHeader() && (this.showBack() || !!this.routeBackPage()),
  );

  public get homeRoute(): string {
    return this.userAssignmentNavigation.defaultHomeRoute(this.sessionUserDataService.user()?.assignmentType);
  }

  @Output() processedCourses: EventEmitter<CourseModel[]> = new EventEmitter<CourseModel[]>();
  @Output() isEmptyfiltered: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() seeker: EventEmitter<string> = new EventEmitter<string>();

  public goBack(link:string): void {
    if(link && link!=='') {
      this.router.navigateByUrl(link)
      return;
    }

    this.location.back();
  }
  
  public search(): void {
    this.seekerSubmit$.next();
  }

  ngOnInit(): void {
    this.initializeNavigationSync();
    this.initializeSeekerEmission();
  }

  private initializeNavigationSync(): void {
    this.syncViewportAndRouteFlags();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncViewportAndRouteFlags());
  }

  private initializeSeekerEmission(): void {
    this.createSeekerTermStream()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((term: string): void => {
        this.seeker.emit(term);
      });
  }

  private createSeekerTermStream(): Observable<string> {
    const seekerControl: FormControl<string | null> = this.coursesForm.controls.seeker;

    return merge(
      seekerControl.valueChanges.pipe(
        map((value: string | null): string => value ?? ""),
        debounceTime(HEADER_SEEKER_DEBOUNCE_MS),
        distinctUntilChanged(),
      ),
      this.seekerSubmit$.pipe(map((): string => seekerControl.value ?? "")),
    ).pipe(distinctUntilChanged());
  }

  private syncViewportAndRouteFlags(): void {
    this.updateMobileViewportFlag();
    this.checkRouteAndScreenSize();
  }

  private checkRouteAndScreenSize(): void {
    const currentUrl: string = this.router.url;
    this.showBell.set(false);
    this.showForumSearch.set(false);
    this.searchPlaceholderKey.set("header.placeholderSearchCourse");

    // Configuramos la visibilidad según la ruta y el tamaño de pantalla
    if (currentUrl.includes('/catalogue')) {
      this.showBell.set(true);
      this.showForumSearch.set(true);
      return;
    }

    if (currentUrl.includes('/induction')) {
      this.showBell.set(true);
      this.showForumSearch.set(true);
      return;
    }

    if (currentUrl.includes('/learning-paths')) {
      this.showBell.set(true);
      this.showForumSearch.set(true);
      return;
    }

    if(currentUrl.includes('/help-forum')){
      this.showBell.set(this.isTabletOrDesktopViewport());
      this.showForumSearch.set(true);
      return;
    }

    if(currentUrl.includes('/course-details')){
      this.showBell.set(this.isTabletOrDesktopViewport());
      return;
    }

    if (currentUrl.includes('/statistics')) {
      this.showBell.set(this.isTabletOrDesktopViewport());
      return;
    }

    if (currentUrl.includes('/certificates')) {
      this.showBell.set(this.isTabletOrDesktopViewport());
      this.showForumSearch.set(true);
      this.searchPlaceholderKey.set("header.placeholderSearchCertificate");
      return;
    }

    if (currentUrl.includes('/settings')) {
      this.showBell.set(true);
      return;
    }
  }

  onResize(_event: unknown): void {
    this.syncViewportAndRouteFlags();
  }

  private updateMobileViewportFlag(): void {
    this.isMobile.set(this.isMobileViewport());
    this.isCompactHeader.set(window.innerWidth <= 768);
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= LAYOUT_MOBILE_BREAKPOINT_PX;
  }

  private isTabletOrDesktopViewport(): boolean {
    return window.innerWidth >= LAYOUT_MOBILE_BREAKPOINT_PX;
  }

  private setSearchVisibility(isVisible: boolean): void {
    const searchBox: HTMLElement | null = document.getElementById('mobileSearchBox');
    const closeIcon: HTMLElement | null = document.getElementById('closeIcon');

    searchBox?.classList.toggle('d-none', !isVisible);
    searchBox?.classList.toggle('d-block', isVisible);
    closeIcon?.classList.toggle('d-none', !isVisible);
    closeIcon?.classList.toggle('d-block', isVisible);
  }

  toggleSearch(): void {
    if (this.isTabletOrDesktopViewport()) {
      this.setSearchVisibility(true);
      return;
    }

    this.isSearchOpen = !this.isSearchOpen;
    this.setSearchVisibility(this.isSearchOpen);
  }
}
